import { createAction } from "@reduxjs/toolkit";
import { DeepPartial } from "redux";
import { Pageable } from "../../services/pageable";
import { FetchableError, FetchableSuccess } from "../common/fetchable/helpers";
import { VerificationDTO as AMLVerificationDTO } from "./models/dto/aml";
import { VerificationDTO as KYCVerificationDTO } from "./models/dto/kyc";
import { VerificationDTO as DueDiligenceVerificationDTO } from "./models/dto/due-diligence";

export namespace VerificationActions {
  export enum ActionTypes {
    getAllKyc = "Verification: Get All Individual Kyc",
    setAllKyc = "Verification: Set All Individual Kyc",
    resetAllKyc = "Verification: Reset All Individual Kyc",
    setAllKycPagination = "Verification: Set All Individual Kyc Pagination",
    getAllSelfKyc = "Verification: Get Self All Individual Kyc",
    setAllSelfKyc = "Verification: Set Self All Individual Kyc",
    resetAllSelfKyc = "Verification: Reset Self All Individual Kyc",
    setAllSelfKycPagination = "Verification: Set Self All Individual Kyc Pagination",
    getSingleKyc = "Verification: Get Single Individual Kyc",
    setSingleKyc = "Verification: Set Single Individual Kyc",

    getAllKyb = "Verification: Get All Business Kyb",
    setAllKyb = "Verification: Set All Business Kyb",
    resetAllKyb = "Verification: Reset All Business Kyb",
    getSingleKyb = "Verification: Get Single Business Kyb",
    setSingleKyb = "Verification: Set Single Business Kyb",
    setAllKybPagination = "Verification: Set All Business Kyb Pagination",

    getAllIndividualAml = "Verification: Get All Individual Aml",
    setAllIndividualAml = "Verification: Set All Individual Aml",
    resetAllIndividualAml = "Verification: Reset All Individual Aml",
    getSingleIndividualAml = "Verification: Get Single Individual Aml",
    setSingleIndividualAml = "Verification: Set Single Individual Aml",
    setAllIndividualAmlPagination = "Verification: Set All Individual Aml Pagination",

    getAllBusinessAml = "Verification: Get All Business Aml",
    setAllBusinessAml = "Verification: Set All Business Aml",
    resetAllBusinessAml = "Verification: Reset All Business Aml",
    getSingleBusinessAml = "Verification: Get Single Business Aml",
    setSingleBusinessAml = "Verification: Set Single Business Aml",
    setAllBusinessAmlPagination = "Verification: Set All Business Aml Pagination",

    getChecksSummary = "Verification: Get Checks Summary",
    setChecksSummary = "Verification: Set Checks Summary",
    resetChecksSummary = "Verification: Reset Checks Summary",

    // getKYBJurisdictions = "Verification: Get KYB Jurisdictions",
    // setKYBJurisdictions = "Verification: Set KYB Jurisdictions",
    // resetKYBJurisdictions = "Verification: Reset KYB Jurisdictions",

    getAllDueDiligenceConfig = "Verification: Get All Due Diligence Config",
    setAllDueDiligenceConfig = "Verification: Set All Due Diligence Config",
    resetAllDueDiligenceConfig = "Verification: Reset All Due Diligence Config",
    setAllDueDiligenceConfigPagination = "Verification: Set All Due Diligence Config Pagination",

    setDueDiligenceForm = "Verification: Set Due Diligence Form",

    getCurrentMerchantConfig = "Users: Get Current Merchant Config",
    setCurrentMerchantConfig = "Users: Set Current Merchant Config",
    resetCurrentMerchantConfig = "Users: Reset Current Merchant Config",
    postCurrentMerchantConfig = "Users: Post Current Merchant Config",
  }

  //------------------------ Kyc
  export const resetAllKyc = createAction(ActionTypes.resetAllKyc);

  export const getAllKyc = createAction(ActionTypes.getAllKyc);

  export const setAllKyc = createAction<
    FetchableSuccess<KYCVerificationDTO.Responses.GetAllKyc.T> | FetchableError,
    ActionTypes.setAllKyc
  >(ActionTypes.setAllKyc);

  export const setAllKycPagination = createAction<
    Pageable.T<
      | "firstName"
      | "lastName"
      | "country"
      | "emailAddress"
      | "userStatus"
      | "createdDate"
    >,
    ActionTypes.setAllKycPagination
  >(ActionTypes.setAllKycPagination);

  export const resetAllSelfKyc = createAction(ActionTypes.resetAllSelfKyc);

  export const getAllSelfKyc = createAction(ActionTypes.getAllSelfKyc);

  export const setAllSelfKyc = createAction<
    FetchableSuccess<KYCVerificationDTO.Responses.GetAllKyc.T> | FetchableError,
    ActionTypes.setAllSelfKyc
  >(ActionTypes.setAllSelfKyc);

  export const setAllSelfKycPagination = createAction<
    Pageable.T<"userStatus" | "createdDate">,
    ActionTypes.setAllSelfKycPagination
  >(ActionTypes.setAllSelfKycPagination);

  export const getSingleKyc = createAction<
    KYCVerificationDTO.Responses.GetSingleKyc.T["fraudCheckId"],
    ActionTypes.getSingleKyc
  >(ActionTypes.getSingleKyc);

  export const setSingleKyc = createAction<
    {
      fraudCheckId: string;
      result: KYCVerificationDTO.Responses.GetSingleKyc.T;
    },
    ActionTypes.setSingleKyc
  >(ActionTypes.setSingleKyc);

  //------------------------ Kyb
  export const resetAllKyb = createAction(ActionTypes.resetAllKyb);

  export const getAllKyb = createAction(ActionTypes.getAllKyb);

  export const setAllKyb = createAction<
    FetchableSuccess<KYCVerificationDTO.Responses.GetAllKyb.T> | FetchableError,
    ActionTypes.setAllKyb
  >(ActionTypes.setAllKyb);

  export const setAllKybPagination = createAction<
    Pageable.T<keyof KYCVerificationDTO.Responses.GetSingleKyb.T>,
    ActionTypes.setAllKybPagination
  >(ActionTypes.setAllKybPagination);

  export const getSingleKyb = createAction<
    KYCVerificationDTO.Responses.GetSingleKyb.T["fraudCheckId"],
    ActionTypes.getSingleKyb
  >(ActionTypes.getSingleKyb);

  export const setSingleKyb = createAction<
    {
      fraudCheckId: string;
      result: KYCVerificationDTO.Responses.GetSingleKyb.T;
    },
    ActionTypes.setSingleKyb
  >(ActionTypes.setSingleKyb);

  //------------------------ Individual AML
  export const resetAllIndividualAml = createAction(
    ActionTypes.resetAllIndividualAml
  );

  export const getAllIndividualAml = createAction(
    ActionTypes.getAllIndividualAml
  );

  export const setAllIndividualAml = createAction<
    | FetchableSuccess<AMLVerificationDTO.Responses.GetAllIndividualAml.T>
    | FetchableError,
    ActionTypes.setAllIndividualAml
  >(ActionTypes.setAllIndividualAml);

  export const setAllIndividualAmlPagination = createAction<
    Pageable.T<
      Exclude<
        keyof AMLVerificationDTO.Responses.GetSingleIndividualAml.T,
        "ongoingChecks" | "fraudCheckId"
      >
    >,
    ActionTypes.setAllIndividualAmlPagination
  >(ActionTypes.setAllIndividualAmlPagination);

  export const getSingleIndividualAml = createAction<
    AMLVerificationDTO.Responses.GetSingleIndividualAml.T["fraudCheckId"],
    ActionTypes.getSingleIndividualAml
  >(ActionTypes.getSingleIndividualAml);

  export const setSingleIndividualAml = createAction<
    {
      fraudCheckId: string;
      result: AMLVerificationDTO.Responses.GetSingleIndividualAml.T;
    },
    ActionTypes.setSingleIndividualAml
  >(ActionTypes.setSingleIndividualAml);

  //------------------------ Business AML
  export const resetAllBusinessAml = createAction(
    ActionTypes.resetAllBusinessAml
  );

  export const getAllBusinessAml = createAction(ActionTypes.getAllBusinessAml);

  export const setAllBusinessAml = createAction<
    | FetchableSuccess<AMLVerificationDTO.Responses.GetAllBusinessAml.T>
    | FetchableError,
    ActionTypes.setAllBusinessAml
  >(ActionTypes.setAllBusinessAml);

  export const setAllBusinessAmlPagination = createAction<
    Pageable.T<keyof AMLVerificationDTO.Responses.GetSingleBusinessAml.T>,
    ActionTypes.setAllBusinessAmlPagination
  >(ActionTypes.setAllBusinessAmlPagination);

  export const getSingleBusinessAml = createAction<
    AMLVerificationDTO.Responses.GetSingleBusinessAml.T["fraudCheckId"],
    ActionTypes.getSingleBusinessAml
  >(ActionTypes.getSingleBusinessAml);

  export const setSingleBusinessAml = createAction<
    {
      fraudCheckId: string;
      result: AMLVerificationDTO.Responses.GetSingleBusinessAml.T;
    },
    ActionTypes.setSingleBusinessAml
  >(ActionTypes.setSingleBusinessAml);

  //------------------------ Checks Summary

  export const getChecksSummary = createAction(ActionTypes.getChecksSummary);

  export const resetChecksSummary = createAction(
    ActionTypes.resetChecksSummary
  );

  export const setChecksSummary = createAction<
    | FetchableSuccess<KYCVerificationDTO.Responses.GetChecksSummary.T>
    | FetchableError,
    ActionTypes.setChecksSummary
  >(ActionTypes.setChecksSummary);

  // export const getKYBJurisdictions = createAction(
  //   ActionTypes.getKYBJurisdictions
  // );

  // export const resetKYBJurisdictions = createAction(
  //   ActionTypes.resetKYBJurisdictions
  // );

  // export const setKYBJurisdictions = createAction<
  //   | FetchableSuccess<KYCVerificationDTO.Responses.GetKYBJurisdictions.T>
  //   | FetchableError,
  //   ActionTypes.setKYBJurisdictions
  // >(ActionTypes.setKYBJurisdictions);

  //------------------------ Due Diligence
  export const resetAllDueDiligenceConfig = createAction(
    ActionTypes.resetAllDueDiligenceConfig
  );

  export const getAllDueDiligenceConfig = createAction(
    ActionTypes.getAllDueDiligenceConfig
  );

  export const setAllDueDiligenceConfig = createAction<
    | FetchableSuccess<DueDiligenceVerificationDTO.Responses.DueDiligence.Config.GetAll.T>
    | FetchableError,
    ActionTypes.setAllDueDiligenceConfig
  >(ActionTypes.setAllDueDiligenceConfig);

  export const setAllDueDiligenceConfigPagination = createAction<
    Pageable.T<
      keyof DueDiligenceVerificationDTO.Responses.DueDiligence.Config.Get.T
    >,
    ActionTypes.setAllDueDiligenceConfigPagination
  >(ActionTypes.setAllDueDiligenceConfigPagination);

  export const setDueDiligenceForm = createAction<
    {
      id: string;
      data: DeepPartial<
        DueDiligenceVerificationDTO.Requests.DueDiligence.Form.Post.Attachments.T &
        DueDiligenceVerificationDTO.Requests.DueDiligence.Form.Post.Final.T &
        DueDiligenceVerificationDTO.Requests.DueDiligence.Form.Post.Init.T
      >;
    },
    ActionTypes.setDueDiligenceForm
  >(ActionTypes.setDueDiligenceForm);

  export const setCurrentMerchantConfig = createAction<
    | FetchableSuccess<KYCVerificationDTO.Responses.GetMerchantConfig.T>
    | FetchableError
  >(ActionTypes.setCurrentMerchantConfig);

  export const getCurrentMerchantConfig = createAction(
    ActionTypes.getCurrentMerchantConfig
  );
  export const resetCurrentMerchantConfig = createAction(
    ActionTypes.resetCurrentMerchantConfig
  );

  export const postCurrentMerchantConfig = createAction<
    KYCVerificationDTO.Responses.GetMerchantConfig.T,
    ActionTypes.postCurrentMerchantConfig
  >(ActionTypes.postCurrentMerchantConfig);

  export type GetAllKyc = ReturnType<typeof getAllKyc>;
  export type SetAllKyc = ReturnType<typeof setAllKyc>;
  export type ResetAllKyc = ReturnType<typeof resetAllKyc>;
  export type SetAllKycPagination = ReturnType<typeof setAllKycPagination>;
  export type GetAllSelfKyc = ReturnType<typeof getAllSelfKyc>;
  export type SetAllSelfKyc = ReturnType<typeof setAllSelfKyc>;
  export type ResetAllSelfKyc = ReturnType<typeof resetAllSelfKyc>;
  export type SetAllSelfKycPagination = ReturnType<
    typeof setAllSelfKycPagination
  >;
  export type GetSingleKyc = ReturnType<typeof getSingleKyc>;
  export type SetSingleKyc = ReturnType<typeof setSingleKyc>;

  export type GetAllKyb = ReturnType<typeof getAllKyb>;
  export type SetAllKyb = ReturnType<typeof setAllKyb>;
  export type GetSingleKyb = ReturnType<typeof getSingleKyb>;
  export type SetSingleKyb = ReturnType<typeof setSingleKyb>;
  export type ResetAllKyb = ReturnType<typeof resetAllKyb>;
  export type SetAllKybPagination = ReturnType<typeof setAllKybPagination>;

  export type ResetAllIndividualAml = ReturnType<typeof resetAllIndividualAml>;
  export type GetAllIndividualAml = ReturnType<typeof getAllIndividualAml>;
  export type SetAllIndividualAml = ReturnType<typeof setAllIndividualAml>;
  export type SetAllIndividualAmlPagination = ReturnType<
    typeof setAllIndividualAmlPagination
  >;
  export type GetSingleIndividualAml = ReturnType<
    typeof getSingleIndividualAml
  >;
  export type SetSingleIndividualAml = ReturnType<
    typeof setSingleIndividualAml
  >;

  export type ResetAllBusinessAml = ReturnType<typeof resetAllBusinessAml>;
  export type GetAllBusinessAml = ReturnType<typeof getAllBusinessAml>;
  export type SetAllBusinessAml = ReturnType<typeof setAllBusinessAml>;
  export type SetAllBusinessAmlPagination = ReturnType<
    typeof setAllBusinessAmlPagination
  >;
  export type GetSingleBusinessAml = ReturnType<typeof getSingleBusinessAml>;
  export type SetSingleBusinessAml = ReturnType<typeof setSingleBusinessAml>;

  export type GetChecksSummary = ReturnType<typeof getChecksSummary>;
  export type SetChecksSummary = ReturnType<typeof setChecksSummary>;
  export type ResetChecksSummary = ReturnType<typeof resetChecksSummary>;

  // export type GetKYBJurisdictions = ReturnType<typeof getKYBJurisdictions>;
  // export type SetKYBJurisdictions = ReturnType<typeof setKYBJurisdictions>;
  // export type ResetKYBJurisdictions = ReturnType<typeof resetKYBJurisdictions>;

  export type ResetAllDueDiligenceConfig = ReturnType<
    typeof resetAllDueDiligenceConfig
  >;
  export type GetAllDueDiligenceConfig = ReturnType<
    typeof getAllDueDiligenceConfig
  >;
  export type SetAllDueDiligenceConfig = ReturnType<
    typeof setAllDueDiligenceConfig
  >;
  export type SetAllDueDiligenceConfigPagination = ReturnType<
    typeof setAllDueDiligenceConfigPagination
  >;
  export type SetCurrentMerchantConfig = ReturnType<
    typeof setCurrentMerchantConfig
  >;
  export type GetCurrentMerchantConfig = ReturnType<
    typeof getCurrentMerchantConfig
  >;
  export type ResetCurrentMerchantConfig = ReturnType<
    typeof resetCurrentMerchantConfig
  >;
  export type PostCurrentMerchantConfig = ReturnType<
    typeof postCurrentMerchantConfig
  >;

  export type SetDueDiligenceForm = ReturnType<typeof setDueDiligenceForm>;

  export type Actions =
    // ---
    | GetAllKyc
    | SetAllKyc
    | ResetAllKyc
    | SetAllKycPagination
    | GetAllSelfKyc
    | SetAllSelfKyc
    | ResetAllSelfKyc
    | SetAllSelfKycPagination
    | GetSingleKyc
    | SetSingleKyc
    // ---
    | GetAllKyb
    | SetAllKyb
    | GetSingleKyb
    | SetSingleKyb
    | ResetAllKyb
    | SetAllKybPagination
    // ---
    | ResetAllIndividualAml
    | GetAllIndividualAml
    | SetAllIndividualAml
    | SetAllIndividualAmlPagination
    | GetSingleIndividualAml
    | SetSingleIndividualAml
    // ---
    | ResetAllBusinessAml
    | GetAllBusinessAml
    | SetAllBusinessAml
    | SetAllBusinessAmlPagination
    | GetSingleBusinessAml
    | SetSingleBusinessAml
    // ---
    | GetChecksSummary
    | SetChecksSummary
    | ResetChecksSummary
    // ---
    // | GetKYBJurisdictions
    // | SetKYBJurisdictions
    // | ResetKYBJurisdictions
    // ---
    | ResetAllDueDiligenceConfig
    | GetAllDueDiligenceConfig
    | SetAllDueDiligenceConfig
    | SetAllDueDiligenceConfigPagination
    | SetDueDiligenceForm
    //
    | SetCurrentMerchantConfig
    | GetCurrentMerchantConfig
    | ResetCurrentMerchantConfig
    | PostCurrentMerchantConfig;
}
